/**
 * Product review moderation queue.
 *
 * Route: /projects/:projectId/reviews
 *
 * Customer reviews land with status='pending'. This queue lets the admin
 * publish, reject, or post a public reply. All operations are scoped to
 * the current project — review data from other projects is never touched.
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Check, MessageSquare, Star, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { apiHelpers, type ProductReview } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm-provider';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const STATUS_COLOR: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  published: 'bg-green-100 text-green-800',
  rejected: 'bg-gray-100 text-gray-800',
};

export default function ReviewsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [ratingFilter, setRatingFilter] = useState<string>('all');
  const [selected, setSelected] = useState<ProductReview | null>(null);
  const [reply, setReply] = useState('');

  const listQuery = useQuery({
    queryKey: ['reviews', projectId, statusFilter, ratingFilter],
    queryFn: async () => {
      const params: any = { limit: 200 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (ratingFilter !== 'all') params.minRating = parseInt(ratingFilter, 10);
      const res = await apiHelpers.listReviews(projectId!, params);
      return (res.data.data || []) as ProductReview[];
    },
    enabled: Boolean(projectId),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['reviews', projectId] });

  const moderateMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'published' | 'rejected' }) =>
      apiHelpers.moderateReview(projectId!, id, { status }),
    onSuccess: (_r, vars) => {
      toast.success(`Review ${vars.status}`);
      invalidate();
    },
    onError: () => toast.error('Failed to moderate'),
  });

  const replyMutation = useMutation({
    mutationFn: () => apiHelpers.replyToReview(projectId!, selected!._id, { body: reply }),
    onSuccess: () => {
      toast.success('Reply posted');
      setReply('');
      invalidate();
      setSelected(null);
    },
    onError: () => toast.error('Failed to post reply'),
  });

  const removeReplyMutation = useMutation({
    mutationFn: (id: string) => apiHelpers.removeReviewReply(projectId!, id),
    onSuccess: () => {
      toast.success('Reply removed');
      invalidate();
    },
    onError: () => toast.error('Failed to remove reply'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiHelpers.deleteReview(projectId!, id),
    onSuccess: () => {
      toast.success('Review deleted');
      invalidate();
      setSelected(null);
    },
    onError: () => toast.error('Failed to delete'),
  });

  const items = listQuery.data || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Product Reviews</h1>
        <p className="text-muted-foreground">
          Moderate customer reviews. Only published reviews appear on your storefront.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {items.filter((r) => r.status === 'pending').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Published</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {items.filter((r) => r.status === 'published').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Rejected</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {items.filter((r) => r.status === 'rejected').length}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Moderation queue</CardTitle>
              <CardDescription>Newest first</CardDescription>
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Select value={ratingFilter} onValueChange={setRatingFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any rating</SelectItem>
                  <SelectItem value="5">5 stars</SelectItem>
                  <SelectItem value="4">4+ stars</SelectItem>
                  <SelectItem value="3">3+ stars</SelectItem>
                  <SelectItem value="1">1+ stars</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {listQuery.isLoading ? (
            <div className="flex justify-center py-8">
              <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-muted-foreground py-12 text-center">No reviews match.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Title / Body</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[200px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => (
                  <TableRow key={r._id} className="hover:bg-muted/50 cursor-pointer">
                    <TableCell onClick={() => setSelected(r)}>
                      <div className="font-medium">{r.customerName || r.customerEmail || '—'}</div>
                      <div className="text-muted-foreground text-xs">{r.customerEmail}</div>
                    </TableCell>
                    <TableCell>
                      <Stars value={r.rating} />
                    </TableCell>
                    <TableCell className="max-w-xs" onClick={() => setSelected(r)}>
                      {r.title && <div className="font-medium">{r.title}</div>}
                      <div className="text-muted-foreground truncate text-sm">{r.body}</div>
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLOR[r.status] || ''}>{r.status}</Badge>
                      {r.reply && (
                        <Badge variant="outline" className="ml-1">
                          Replied
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{format(new Date(r.createdAt), 'MMM d, yyyy')}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {r.status !== 'published' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              moderateMutation.mutate({ id: r._id, status: 'published' });
                            }}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        )}
                        {r.status !== 'rejected' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              moderateMutation.mutate({ id: r._id, status: 'rejected' });
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelected(r);
                            setReply(r.reply?.body || '');
                          }}
                        >
                          <MessageSquare className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={Boolean(selected)}
        onOpenChange={(open) => {
          if (!open) {
            setSelected(null);
            setReply('');
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Review detail</DialogTitle>
            <DialogDescription>
              {selected &&
                `${selected.customerName || selected.customerEmail || '—'} · ${format(
                  new Date(selected.createdAt),
                  'MMM d, yyyy'
                )}`}
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Stars value={selected.rating} />
                <Badge className={STATUS_COLOR[selected.status] || ''}>{selected.status}</Badge>
              </div>
              {selected.title && <div className="text-lg font-semibold">{selected.title}</div>}
              <div className="bg-muted/40 whitespace-pre-wrap rounded-md border p-3 text-sm">
                {selected.body}
              </div>
              {selected.moderationNote && (
                <div>
                  <Label className="text-muted-foreground text-xs">Moderation note</Label>
                  <p className="mt-1 text-sm">{selected.moderationNote}</p>
                </div>
              )}
              <div>
                <Label htmlFor="reply">Public reply</Label>
                <Textarea
                  id="reply"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  rows={4}
                  maxLength={2000}
                  placeholder="Thanks for your feedback…"
                  className="mt-1"
                />
                <div className="mt-2 flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => replyMutation.mutate()}
                    disabled={!reply.trim() || replyMutation.isPending}
                  >
                    Post reply
                  </Button>
                  {selected.reply && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => removeReplyMutation.mutate(selected._id)}
                    >
                      Remove reply
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="destructive"
              onClick={async () => {
                if (
                  selected &&
                  (await confirm({
                    title: 'Delete review?',
                    message: 'Permanently delete this review?',
                    danger: true,
                  }))
                ) {
                  deleteMutation.mutate(selected._id);
                }
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stars({ value }: { value: number }) {
  return (
    <div className="flex">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`h-4 w-4 ${n <= value ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
        />
      ))}
    </div>
  );
}
